import { Header } from "@/components/Header";
import { LeadForm } from "@/components/LeadForm";
import { Features } from "@/components/Features";
import { Testimonials } from "@/components/Testimonials";
import { Gallery } from "@/components/Gallery";
import { CTA } from "@/components/CTA";
import { Footer } from "@/components/Footer";
import { PaymentEstimator } from "@/components/PaymentEstimator";
import { SEOHead } from "@/components/SEOHead";
import { CheckCircle, ArrowDown, Shield, Accessibility, Droplets, Sparkles, ArrowLeftRight } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1631048835379-c7f36f9410dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHx3YWxrJTIwaW4lMjBzaG93ZXIlMjBnbGFzcyUyMGRvb3IlMjBtb2Rlcm4lMjBiYXRocm9vbXxlbnwwfHx8fDE3NzAzMTgwMzJ8MA&ixlib=rb-4.1.0&q=85";

const keyPoints = [
  "Tub-to-Shower Conversions - Modern Walk-In Showers",
  "Shower-to-Tub Conversions - Add a Soaking Tub",
  "Frameless Glass Doors & Premium Fixtures",
  "Safety Features: Grab Bars & Slip-Resistant Floors",
  "Most Projects Completed in Just One Day"
];

const conversionFeatures = [
  {
    icon: ArrowLeftRight,
    title: "Any Conversion, Any Direction",
    description: "Whether you want to replace your tub with a shower or add a tub where your shower is, we handle both conversions expertly."
  },
  {
    icon: Accessibility,
    title: "Accessibility Options",
    description: "Low step-in thresholds, built-in seating, and grab bars make bathing safer for all ages and mobility levels."
  },
  {
    icon: Droplets,
    title: "Premium Fixtures & Finishes",
    description: "Rainfall showerheads, handheld sprayers, modern faucets, and cultured marble surrounds for a luxurious look."
  },
  {
    icon: Sparkles,
    title: "Easy-Clean Surfaces",
    description: "Acrylic and tile surfaces that resist mold, mildew, and soap scum - cleaning takes minutes, not hours."
  }
];

export default function TubShowerConversions() {
  return (
    <div className="min-h-screen" data-testid="tub-shower-conversions-page">
      <SEOHead page="tub-shower-conversions" />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        >
          <div className="hero-overlay absolute inset-0" />
        </div>

        <Header />

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-white animate-fade-in">
              <span className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Tub & Shower Conversion Experts
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Playfair_Display'] leading-tight" data-testid="hero-headline">
                Tub & Shower Conversions in New Mexico
              </h1>
              <p className="text-xl text-white/90 mb-8 max-w-xl">
                Convert your tub to a walk-in shower or your shower to a relaxing tub. Professional installation completed in as little as one day.
              </p>

              {/* Key Points */}
              <ul className="space-y-3 mb-8">
                {keyPoints.map((point, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-amber-400 flex-shrink-0" />
                    <span className="text-white/90">{point}</span>
                  </li>
                ))}
              </ul>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 items-center">
                <div className="trust-badge bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">1-Day Installation</span>
                </div>
                <div className="trust-badge bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">Lifetime Warranty</span>
                </div>
                <div className="trust-badge bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <span className="text-sm font-semibold">Free Estimates</span>
                </div>
              </div>
            </div>

            {/* Right - Lead Form + Call CTA */}
            <div className="animate-slide-in-right space-y-6">
              <LeadForm pageType="tub-shower-conversion" title="Get $1500 Off & Schedule an Appointment" />
              
              {/* Call CTA Section */}
              <div className="glass-form p-6 rounded-2xl shadow-2xl border border-gray-100 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Playfair_Display']">
                  Get $2500 Off Your Project When You Call Now
                </h3>
                <p className="text-gray-700 font-medium mb-3">
                  Limited spots available: Schedule Your In-Home Appointment
                </p>
                <a
                  href="tel:+15054727064"
                  className="inline-flex items-center justify-center w-full h-14 bg-[#1E4C9A] hover:bg-[#153975] text-white font-bold text-xl rounded-full shadow-lg hover:shadow-xl transition-all duration-300 mb-2"
                  data-testid="call-cta-button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  +1 (505) 472-7064
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ArrowDown className="h-6 w-6 text-white/70" />
        </div>
      </section>

      {/* Conversion Types Section */}
      <section className="py-20 bg-white" data-testid="conversion-types-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#1E4C9A] font-semibold text-sm uppercase tracking-wider">
              Our Conversion Services
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4 font-['Playfair_Display']">
              Tub-to-Shower or Shower-to-Tub?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We specialize in both directions - whatever your bathroom needs, we've got you covered.
            </p>
            <div className="section-divider mx-auto mt-4" />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Tub to Shower */}
            <div className="feature-card bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-['Playfair_Display']">
                Tub-to-Shower Conversion
              </h3>
              <p className="text-gray-600 mb-6">
                Remove your old, unused bathtub and replace it with a spacious, modern walk-in shower. Perfect for those who prefer quick showers and want easier accessibility.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="h-4 w-4 text-[#1E4C9A]" />
                  Low step-in threshold for easy entry
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="h-4 w-4 text-[#1E4C9A]" />
                  Frameless glass doors available
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="h-4 w-4 text-[#1E4C9A]" />
                  Built-in shelving & seating options
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="h-4 w-4 text-[#1E4C9A]" />
                  Multiple showerhead configurations
                </li>
              </ul>
            </div>

            {/* Shower to Tub */}
            <div className="feature-card bg-[#F9FAFB] p-8 rounded-xl border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 font-['Playfair_Display']">
                Shower-to-Tub Conversion
              </h3>
              <p className="text-gray-600 mb-6">
                Transform your shower into a relaxing soaking tub. Ideal for families with children, those who enjoy baths, or adding resale value to your home.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="h-4 w-4 text-[#1E4C9A]" />
                  Deep soaking tub options
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="h-4 w-4 text-[#1E4C9A]" />
                  Tub/shower combo available
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="h-4 w-4 text-[#1E4C9A]" />
                  Freestanding & alcove styles
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="h-4 w-4 text-[#1E4C9A]" />
                  Hydrotherapy jets optional
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#F9FAFB]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#1E4C9A] font-semibold text-sm uppercase tracking-wider">
              Why Convert?
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4 font-['Playfair_Display']">
              Benefits of Bathroom Conversions
            </h2>
            <div className="section-divider mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {conversionFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`feature-card bg-white p-6 rounded-xl border border-gray-100 text-center animate-slide-up delay-${(index + 1) * 100}`}
                >
                  <div className="w-14 h-14 bg-[#1E4C9A]/10 rounded-xl flex items-center justify-center mx-auto mb-5">
                    <Icon className="h-7 w-7 text-[#1E4C9A]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 font-['Playfair_Display']">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <Features title="Why Choose Prime Baths for Your Conversion?" />

      {/* Payment Estimator */}
      <PaymentEstimator defaultProjectType="tub-shower-conversion" />

      {/* Gallery Section */}
      <Gallery serviceType="tub-shower-conversion" />

      {/* Testimonials Section */}
      <Testimonials />

      {/* CTA Section */}
      <CTA 
        title="Ready for Your Bathroom Conversion?"
        subtitle="Get $1500 off your tub or shower conversion. Schedule your free in-home consultation today."
      />

      {/* Footer */}
      <Footer />
    </div>
  );
}
